import { useRef, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';
import { PAGE_SEO } from '../../lib/seo';
import { motion, useInView } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
    Moon,
    Phone,
    Truck,
    CheckCircle,
    ArrowRight,
    Clock,
    DollarSign,
    AlertCircle,
    PhoneOff,
    Bell,
    Users,
    Calendar,
    TrendingUp
} from 'lucide-react';

const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

function AnimatedSection({ children, className = "", delay = 0 }) {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-80px" });
    return (
        <motion.div
            ref={ref}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={{
                hidden: { opacity: 0, y: 50 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay, ease: "easeOut" } }
            }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

export default function AfterHoursTowingDispatchPage() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const softwareAppSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Towmatic After Hours Dispatch",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "description": "AI-powered after-hours towing dispatch that answers every call overnight, dispatches drivers, and handles the full job lifecycle while you sleep.",
        "url": "https://towmatic.ai/after-hours-tow-answering-service",
        "offers": {
            "@type": "Offer",
            "url": "https://towmatic.ai/pricing"
        },
        "creator": {
            "@type": "Organization",
            "name": "Towmatic",
            "url": "https://towmatic.ai"
        }
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
                "@type": "Question",
                "name": "How does after-hours dispatch work with Towmatic?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Towmatic's AI answers calls 24/7, gathers job details through natural conversation, and dispatches your drivers via text, phone call, and email. If a driver doesn't respond, it automatically escalates to the next driver."
                }
            },
            {
                "@type": "Question",
                "name": "What if my driver doesn't respond to a night call?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Towmatic automatically escalates to the next available driver in your rotation. You're notified at every step so nothing falls through the cracks."
                }
            }
        ]
    };

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://towmatic.ai/" },
            { "@type": "ListItem", "position": 2, "name": "After Hours Towing Dispatch", "item": "https://towmatic.ai/after-hours-tow-answering-service" }
        ]
    };

    return (
        <div className="min-h-screen bg-black text-white overflow-x-hidden">
            <SEOHead
                title={PAGE_SEO.afterHoursTowingDispatch.title}
                description={PAGE_SEO.afterHoursTowingDispatch.description}
                keywords={PAGE_SEO.afterHoursTowingDispatch.keywords}
                canonicalUrl="https://towmatic.ai/after-hours-tow-answering-service"
                noIndex={PAGE_SEO.afterHoursTowingDispatch.noIndex}
            />
            <>
                <script type="application/ld+json">{JSON.stringify(softwareAppSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
            </>

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-zinc-800/50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <img src="/towmatic-logo-white.png" alt="Towmatic After Hours Dispatch" className="h-8" />
                    </Link>
                    <div className="hidden md:flex items-center gap-8">
                        <Link to="/ai-towing-dispatch" className="text-zinc-400 hover:text-white transition text-sm font-medium">AI Dispatch</Link>
                        <Link to="/ai-voice-agent-tow-dispatch" className="text-zinc-400 hover:text-white transition text-sm font-medium">Voice AI</Link>
                        <Link to="/towing-dispatch-software-comparison" className="text-zinc-400 hover:text-white transition text-sm font-medium">Compare</Link>
                        <Link to="/pricing" className="text-zinc-400 hover:text-white transition text-sm font-medium">Pricing</Link>
                    </div>
                    <div className="flex items-center gap-4">
                        <Button onClick={() => navigate('/login')} variant="ghost" className="text-zinc-300 hover:text-white hidden sm:inline-flex">Login</Button>
                        <Button onClick={() => navigate('/signup')} className="bg-orange-500 hover:bg-orange-600 text-white px-6 font-semibold">Get Started</Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 via-transparent to-zinc-900/50" />
                
                <div className="relative z-10 max-w-7xl mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <div className="inline-flex items-center gap-2 bg-indigo-500/20 border border-indigo-500/30 rounded-full px-4 py-2 mb-6">
                                <Moon className="w-4 h-4 text-indigo-400" />
                                <span className="text-indigo-400 text-sm font-semibold">24/7 Coverage</span>
                            </div>

                            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-[1.1] tracking-tight">
                                After-Hours Towing Dispatch That{' '}
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-blue-500">
                                    Never Sleeps
                                </span>
                            </h2>

                            <p className="text-lg text-zinc-400 mb-8 leading-relaxed">
                                The most profitable tow calls happen when you're least equipped to answer them. Towmatic handles your overnight dispatch — answering calls, qualifying jobs, and getting your drivers rolling — so you stop losing revenue to voicemail.
                            </p>

                            <div className="flex flex-wrap gap-4">
                                <Button
                                    onClick={() => navigate('/pricing')}
                                    size="lg"
                                    className="bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white px-8 py-6 text-lg rounded-xl font-bold"
                                    data-testid="hero-pricing-btn"
                                >
                                    See Our Pricing <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                                <Button
                                    onClick={() => navigate('/contact')}
                                    variant="outline"
                                    size="lg"
                                    className="border-zinc-700 text-white hover:bg-zinc-800 px-8 py-6 text-lg rounded-xl"
                                    data-testid="hero-contact-btn"
                                >
                                    Contact Us
                                </Button>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500/30 to-blue-500/20 rounded-3xl blur-2xl" />
                            <img
                                src="https://images.unsplash.com/photo-1616729159432-1ea9d9b2043e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxuaWdodCUyMGVtZXJnZW5jeSUyMHZlaGljbGUlMjBmbGFzaGluZyUyMGxpZ2h0c3xlbnwwfHx8fDE3NzIwNDAxMzB8MA&ixlib=rb-4.1.0&q=85"
                                alt="Emergency vehicle with lights at night - after hours towing dispatch"
                                className="relative rounded-2xl shadow-2xl border border-zinc-800 w-full"
                            />
                            <motion.div
                                animate={{ y: [0, -10, 0] }}
                                transition={{ duration: 3, repeat: Infinity }}
                                className="absolute -bottom-6 -right-6 bg-zinc-900 border border-zinc-800 rounded-xl p-4 shadow-xl"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-indigo-500/20 rounded-full flex items-center justify-center">
                                        <Clock className="w-6 h-6 text-indigo-500" />
                                    </div>
                                    <div>
                                        <p className="text-white font-bold">2:47 AM</p>
                                        <p className="text-zinc-500 text-sm">Call answered instantly</p>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* The Problem Section */}
            <section className="py-20 bg-gradient-to-b from-black to-zinc-900/50">
                <div className="max-w-4xl mx-auto px-6">
                    <AnimatedSection>
                        <h2 className="text-3xl sm:text-4xl font-black mb-6 tracking-tight">
                            Your Most Profitable Calls Come at Your Worst Hours
                        </h2>
                        <div className="space-y-6 text-lg text-zinc-400 leading-relaxed">
                            <p>
                                Breakdowns don't wait for business hours. Neither do accidents, lockouts, or police rotations. The calls that come in between 10 PM and 6 AM are often the highest-margin jobs — emergency roadside, overnight impounds, and stranded drivers who need help now.
                            </p>
                            <p>
                                But if your phone goes to voicemail at 3 AM, that caller isn't leaving a message. They're calling your competitor. And in the towing business, that's a customer you may never see again.
                            </p>
                        </div>
                    </AnimatedSection>

                    <AnimatedSection delay={0.2}>
                        <div className="mt-12 grid md:grid-cols-3 gap-6">
                            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                                <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mb-4">
                                    <DollarSign className="w-6 h-6 text-red-500" />
                                </div>
                                <h3 className="text-lg font-bold text-white mb-2">Option 1: Hire an Overnight Dispatcher</h3>
                                <p className="text-zinc-400 text-sm">$40,000–$60,000/year in salary, plus management overhead, training, and the reality that people don't want to work 3 AM shifts.</p>
                            </div>
                            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                                <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mb-4">
                                    <PhoneOff className="w-6 h-6 text-red-500" />
                                </div>
                                <h3 className="text-lg font-bold text-white mb-2">Option 2: Answer Calls Yourself</h3>
                                <p className="text-zinc-400 text-sm">You become the overnight dispatcher. You're exhausted. You miss calls. Your family suffers. This doesn't scale.</p>
                            </div>
                            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                                    <CheckCircle className="w-6 h-6 text-green-500" />
                                </div>
                                <h3 className="text-lg font-bold text-white mb-2">Option 3: Towmatic</h3>
                                <p className="text-zinc-400 text-sm">AI dispatch that answers every call, dispatches your drivers, and handles the full job lifecycle — for a fixed monthly cost.</p>
                            </div>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* How It Works Timeline */}
            <section className="py-20">
                <div className="max-w-4xl mx-auto px-6">
                    <AnimatedSection className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-black mb-6 tracking-tight">
                            What Happens While You Sleep
                        </h2>
                    </AnimatedSection>

                    <div className="space-y-8">
                        {[
                            { time: '10:47 PM', title: 'A Call Comes In', desc: "Towmatic answers immediately. The AI engages in natural conversation, confirming the caller's location, vehicle, and what they need." },
                            { time: '10:49 PM', title: 'Your Driver Gets Notified', desc: "Job details are sent via text, phone call, and email — all at the same time. At 3 AM, your driver isn't checking their inbox. The phone call wakes them up." },
                            { time: '10:51 PM', title: 'Driver Confirms', desc: "Towmatic logs the confirmation and timestamps the response. You see everything on your dashboard." },
                            { time: 'If No Response', title: 'Automatic Escalation', desc: "If the first driver doesn't confirm within your set window, Towmatic automatically dispatches to the next driver in your rotation — and notifies you." },
                        ].map((item, idx) => (
                            <AnimatedSection key={idx} delay={idx * 0.1}>
                                <div className="flex gap-6">
                                    <div className="w-24 flex-shrink-0 text-right">
                                        <span className="text-indigo-400 font-mono font-bold">{item.time}</span>
                                    </div>
                                    <div className="w-4 flex-shrink-0 flex flex-col items-center">
                                        <div className="w-4 h-4 bg-indigo-500 rounded-full" />
                                        {idx < 3 && <div className="w-0.5 h-full bg-indigo-500/30 mt-2" />}
                                    </div>
                                    <div className="flex-1 pb-8">
                                        <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                                        <p className="text-zinc-400">{item.desc}</p>
                                    </div>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* Cost Section */}
            <section className="py-20 bg-gradient-to-b from-zinc-900/50 to-black">
                <div className="max-w-4xl mx-auto px-6">
                    <AnimatedSection>
                        <h2 className="text-3xl sm:text-4xl font-black mb-6 tracking-tight">
                            Every Missed Call Has a Price Tag
                        </h2>
                        <div className="space-y-6 text-lg text-zinc-400 leading-relaxed">
                            <p>
                                A single missed after-hours call can cost you $150–$350 in direct revenue. Miss just 2–3 calls a week, and you're leaving $15,000–$36,000 on the table every year.
                            </p>
                            <p>
                                But the indirect cost is worse. When a customer calls you at 2 AM and gets voicemail, they learn to call someone else. They tell their friends. You're training your market to use your competitor.
                            </p>
                            <p>
                                If you're on a police rotation, missed calls can cost you your spot. The city doesn't care why you missed the call — they just know you didn't answer.
                            </p>
                        </div>
                    </AnimatedSection>

                    <AnimatedSection delay={0.2}>
                        <div className="mt-12 bg-gradient-to-r from-indigo-500/10 to-blue-500/10 border border-indigo-500/30 rounded-2xl p-8">
                            <h3 className="text-2xl font-bold text-white mb-4">The Math is Simple</h3>
                            <div className="grid md:grid-cols-3 gap-6 text-center">
                                <div>
                                    <p className="text-4xl font-black text-indigo-400">$250</p>
                                    <p className="text-zinc-400 text-sm mt-2">Avg. tow job value</p>
                                </div>
                                <div>
                                    <p className="text-4xl font-black text-indigo-400">3/week</p>
                                    <p className="text-zinc-400 text-sm mt-2">Missed night calls</p>
                                </div>
                                <div>
                                    <p className="text-4xl font-black text-red-400">$39k/yr</p>
                                    <p className="text-zinc-400 text-sm mt-2">Lost revenue</p>
                                </div>
                            </div>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* More Than Answering Section */}
            <section className="py-20">
                <div className="max-w-4xl mx-auto px-6">
                    <AnimatedSection>
                        <h2 className="text-3xl sm:text-4xl font-black mb-6 tracking-tight">
                            Towmatic Doesn't Just Answer — It Dispatches
                        </h2>
                        <div className="space-y-6 text-lg text-zinc-400 leading-relaxed">
                            <p>
                                Traditional answering services take a message and email it to you. You still have to wake up, read the message, call your driver, and coordinate the job. That's not solving the problem — it's just moving it.
                            </p>
                            <p className="text-white font-semibold">
                                Towmatic handles the entire chain: Answer → Qualify → Dispatch → Notify → Confirm → Escalate. By the time you wake up, the job is already in progress.
                            </p>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="py-20 bg-gradient-to-b from-black to-zinc-900/50">
                <div className="max-w-4xl mx-auto px-6">
                    <AnimatedSection>
                        <h2 className="text-3xl sm:text-4xl font-black mb-6 tracking-tight">
                            Predictable Pricing That Doesn't Punish Slow Months
                        </h2>
                        <div className="space-y-6 text-lg text-zinc-400 leading-relaxed">
                            <p>
                                Towing is cyclical. Some months are packed; others are dead. A full-time overnight dispatcher is a fixed cost that doesn't scale down when business slows.
                            </p>
                            <p>
                                Towmatic gives you professional after-hours dispatch at a fixed monthly cost that's a fraction of a dispatcher's salary — and it works around the clock without overtime, benefits, or sick days.
                            </p>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <Button
                                onClick={() => navigate('/pricing')}
                                size="lg"
                                className="bg-indigo-500 hover:bg-indigo-600 text-white px-8 py-6 text-lg rounded-xl"
                            >
                                See Our Pricing <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                            <Button
                                onClick={() => navigate('/contact')}
                                variant="outline"
                                size="lg"
                                className="border-zinc-700 text-white hover:bg-zinc-800 px-8 py-6 text-lg rounded-xl"
                            >
                                Contact Us
                            </Button>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* Internal Links */}
            <section className="py-16 border-t border-zinc-800">
                <div className="max-w-7xl mx-auto px-6">
                    <h3 className="text-lg font-bold text-zinc-500 mb-6 text-center">Explore More</h3>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/ai-towing-dispatch" className="px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 hover:border-indigo-500/50 hover:text-white transition">AI Towing Dispatch</Link>
                        <Link to="/ai-voice-agent-tow-dispatch" className="px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 hover:border-indigo-500/50 hover:text-white transition">Voice AI Dispatch</Link>
                        <Link to="/towing-dispatch-software-comparison" className="px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 hover:border-indigo-500/50 hover:text-white transition">Software Comparison</Link>
                        <Link to="/web-forms" className="px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 hover:border-indigo-500/50 hover:text-white transition">Web Forms</Link>
                        <Link to="/blog" className="px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 hover:border-indigo-500/50 hover:text-white transition">Blog</Link>
                        <Link to="/pricing" className="px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 hover:border-indigo-500/50 hover:text-white transition">Pricing</Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 bg-black border-t border-zinc-800">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <Link to="/"><img src="/towmatic-logo-white.png" alt="Towmatic" className="h-8" /></Link>
                        <div className="flex items-center gap-6 text-sm text-zinc-500">
                            <Link to="/privacy-policy" className="hover:text-white transition">Privacy</Link>
                            <Link to="/terms-and-conditions" className="hover:text-white transition">Terms</Link>
                            <Link to="/contact" className="hover:text-white transition">Contact</Link>
                            <Link to="/blog" className="hover:text-white transition">Blog</Link>
                        </div>
                        <p className="text-zinc-600 text-sm">© {new Date().getFullYear()} Towmatic. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
